import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { PortalAuthService } from '../../core/auth/portal-auth.service';
import { LegalTocSection, LegalTocComponent } from '../../core/components/legal-toc/legal-toc.component';
import { LanguageService } from '../../core/core-services/language.service';
import { SeoService } from '../../core/services/seo.service';
import { AnnexProduct, ANNEX_PRODUCTS, ResolvedProductDoc, allTermsDocs } from '../legal/product-legal.registry';
import { buildProductToc } from '../legal/legal-toc-builder';
import { PortalNavbarComponent } from '../../core/components/portal-navbar/portal-navbar.component';
import { IonicModule } from '@ionic/angular';
import { PortalFooterComponent } from '../../core/components/portal-footer/portal-footer.component';

/** Section count of the template's base § stack — keep in sync when adding/removing a §. */
const SECTION_COUNT = 23;

@Component({
    selector: 'app-terms',
    templateUrl: './terms.page.html',
    styleUrls: ['./terms.page.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [PortalNavbarComponent, IonicModule, LegalTocComponent, RouterLink, PortalFooterComponent, TranslatePipe]
})
export class TermsPage implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild(LegalTocComponent) private toc?: LegalTocComponent;

    /** Products with a short annex card, always rendered — see docs/KONZEPT-Produkt-Rechtsseiten.md
     *  for why this page no longer switches on a `?product=` parameter. */
    readonly annexProducts: readonly AnnexProduct[] = ANNEX_PRODUCTS;

    /**
     * Product terms annexes (ScreenDeck, Chroniq), always rendered before the base §§.
     * These do NOT replace the base § stack: the base AGB is the contract and every
     * annex only supplements it (see product-legal.registry.ts).
     */
    productDocs: readonly ResolvedProductDoc[] = [];

    /** Drives the header variant: app-portal-navbar while signed out; signed in, the
     *  app shell's own header takes over instead (see AppComponent.hideAppShell$). */
    isAuthenticated = false;

    /** Entries of the section index (app-legal-toc). § N carries id="agb-sN" and the
     *  heading 'mainauth.terms.sNTitle'; an annex section carries id="agb-<product>p<num>"
     *  and its own plain-text title. */
    readonly baseTocSections: readonly LegalTocSection[] = Array.from({ length: SECTION_COUNT }, (_, i) => ({
        id: `agb-s${i + 1}`,
        num: `§ ${i + 1}`,
        titleKey: `mainauth.terms.s${i + 1}Title`,
    }));

    /** Divided by product: the group-wide base §§ first, then one block per product (see buildProductToc). */
    tocSections: readonly LegalTocSection[] = this.baseTocSections;

    private readonly destroy$ = new Subject<void>();
    private readonly titleKey = 'mainauth.terms.navTitle';
    private readonly metaKey = 'mainauth.terms.meta';
    /**
     * ?lang= — the language the reader was already using on the linking platform.
     * Only pins the annexes until the reader picks a language themselves; cleared on the
     * first onLangChange so the switcher is never dead against a URL parameter.
     */
    private requestedLang: string | null;

    /** ?product= is gone, but a plain #fragment (e.g. from an external app's
     *  "#tablestack" link) still has to land the reader on that section — see
     *  ngAfterViewInit and legal-toc.component.ts's scrollToId(). */
    private readonly fragment: string | null;

    constructor(
        route: ActivatedRoute,
        private readonly titleService: Title,
        private readonly meta: Meta,
        private readonly seo: SeoService,
        private readonly translate: TranslateService,
        private readonly language: LanguageService,
        private readonly portalAuth: PortalAuthService,
        private readonly cdr: ChangeDetectorRef,
    ) {
        this.requestedLang = route.snapshot.queryParamMap.get('lang');
        this.fragment = route.snapshot.fragment;
        this.applyProductDocs();
    }

    ngAfterViewInit(): void {
        if (this.fragment) {
            void this.toc?.scrollToId(this.fragment);
        }
    }

    ngOnInit(): void {
        // <title>/<meta name="description"> (docs/KONZEPT-Produkt-Rechtsseiten.md).
        // translated$() never yields raw i18n keys, so a direct/crawler landing doesn't get them as
        // its title, and it re-runs on a language switch and when a translation file arrives late.
        this.seo.translated$([this.titleKey, this.metaKey]).pipe(takeUntil(this.destroy$))
            .subscribe(t => this.applySeo(t));
        this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.applyProductDocs();
            this.cdr.markForCheck();
        });

        // A reader who works the switcher wants this page in that language, so the URL
        // parameter stops deciding. Without this an fr/it annex would stay pinned for the
        // rest of the visit and the switcher would look broken on this page alone.
        this.language.userSelection$.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.requestedLang = null;
        });

        // The annexes do not replace the base § stack — the two render on top of each
        // other. So a ?lang= the portal UI itself ships has to reach the §§ as well, or
        // the reader gets an annex in one language and the contract it annexes in
        // another. Adopting it makes the pin redundant, so it goes. fr/it have no
        // translation file: those stay pinned and the §§ keep the session language,
        // which is the tradeoff legal-doc.model.ts documents.
        if (this.requestedLang && this.language.languages.includes(this.requestedLang)) {
            this.language.useForSession(this.requestedLang);
            this.requestedLang = null;
        }

        this.portalAuth.isAuthenticated$.pipe(takeUntil(this.destroy$)).subscribe(auth => {
            this.isAuthenticated = auth;
            this.cdr.markForCheck();
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /** DOM id of an annex section. Used by both the template and the index, so the two
     *  cannot drift; namespaced by product because ScreenDeck and Chroniq both number
     *  their sections 'A 1', 'A 2', … independently. */
    sectionId(product: string, num: string): string {
        return `agb-${product}p${num.toLowerCase().replace(/[^a-z0-9]/g, '')}`;
    }

    /** Resolves every annex document and rebuilds the section index, divided by product. */
    private applyProductDocs(): void {
        this.productDocs = allTermsDocs(this.requestedLang, this.translate.currentLang);
        this.tocSections = buildProductToc({
            generalKey: 'mainauth.terms.title',
            baseSections: this.baseTocSections,
            docs: this.productDocs,
            sectionId: (product, num) => this.sectionId(product, num),
            annexTitleKey: product => `mainauth.terms.product.${product}.title`,
        });
    }

    private applySeo(t: Record<string, string>): void {
        this.titleService.setTitle(`${t[this.titleKey]} | Gastro Management`);
        this.meta.updateTag({ name: 'description', content: truncate(t[this.metaKey]) });
    }
}

function truncate(text: string, max = 155): string {
    return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
}
